import React from 'react';
import { Flex, Pagination, Empty, Tag, Radio, Input, Modal, Form, Button } from 'antd';
import { FormOutlined, BellOutlined, SearchOutlined } from '@ant-design/icons';
import AppIconGroup from '../../components/app-icon-group';
import SwitchShop from '../shop/switch-shop';
import OrderCard from './card';
import { getCurrentShopId } from '../shop/get-current-shop';

export default () => {
    const [orderList, setOrderList] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [shopId, setShopId] = React.useState(null);
    const [searchForm] = Form.useForm();
    const url = new URL(window.location);
    const [page, setPage] = React.useState(url.searchParams.get('page') || 1);

    React.useEffect(() => {
        const fetchShopId = async () => {
            const id = await getCurrentShopId();
            setShopId(id);
        };
        
        fetchShopId();
    }, []);

    React.useEffect(() => {
        if (shopId) {
            loadOrderList();
        }
    }, [shopId]);

    const getOrderList = async () => {
        const response = await fetch(`/api/order/list?shopId=${shopId}&${url.searchParams.toString()}`);
        return await response.json();
    }

    const loadOrderList = async (page = 1) => {
        url.searchParams.set('page', page);
        setPage(page);
        const res = await getOrderList(page);
        setOrderList(res?.data?.rows || []);
        setTotal(res?.data?.count || 0);
    }

    const onSearch = async () => {
        const content = <Form form={searchForm}>
            <Form.Item name='searchType' style={{marginBottom: '10px'}}>
                <Radio.Group defaultValue='address' size="middle" optionType='button'>
                    <Radio value="address">地址</Radio>
                    <Radio value="phoneNo">电话</Radio>
                    <Radio value="orderNo">订单号</Radio>
                </Radio.Group>
            </Form.Item>
            <Form.Item name='keyword'>
                <Input placeholder="请输入关键词" />
            </Form.Item>
        </Form>

        Modal.confirm({
            title: '搜索订单',
            icon: null,
            content,
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                const {keyword, searchType = 'address'} = searchForm.getFieldsValue();
                if(keyword && searchType) {
                    url.searchParams.set(searchType, keyword.trim());
                    url.searchParams.set('page', 1);
                    window.history.pushState({}, "", url);

                    loadOrderList();
                }
            }
        })
    }

    const onKeywordRemove = async (key, value) => {
        console.log(key, value);
        url.searchParams.delete(key);
        url.searchParams.set('page', 1);
        window.history.pushState({}, "", url);

        loadOrderList();
    }

    const onStatusChange = async (status) => {
        if(status === 'all') {
            url.searchParams.delete('status');
        } else {
            url.searchParams.set('status', status);
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, "", url);

        loadOrderList();
    }
    
    return (
        <div>
            {/* 顶部操作区 */}
            <div className="pt-4 pb-3">
                <SwitchShop />
                
                {/* 快捷操作 - iOS 风格图标组 */}
                <div className="mt-3">
                    <AppIconGroup 
                      items={[
                        {
                          id: 1,
                          href: '/order/create',
                          title: '创建订单',
                          icon: <FormOutlined />,
                          color: 'from-blue-500 to-blue-600'
                        },
                        {
                          id: 2,
                          href: '/order/notify-setting',
                          title: '通知设置',
                          icon: <BellOutlined />,
                          color: 'from-green-500 to-green-600'
                        }
                      ]}
                      cols="2-2-4-4-4"
                    />
                </div>
            </div>

            {/* 标题和搜索栏 */}
            <div className="py-3 border-b border-gray-100">
                <Flex gap={3} align='center' justify='space-between' className="mb-3">
                    <h2 className="text-lg md:text-xl font-bold text-gray-800 m-0">订单列表</h2>
                    <Button 
                        type="primary" 
                        icon={<SearchOutlined />} 
                        onClick={onSearch}
                        size="small"
                        className="rounded-md active:scale-95 transition-transform text-s"
                    >
                        搜索
                    </Button>
                </Flex>

                {/* 搜索标签 */}
                <div className="flex flex-wrap gap-2 mb-3">
                    {
                        Array.from(url.searchParams).filter(([key]) => ['orderNo', 'phoneNo', 'address'].includes(key)).map(([key, value]) => {
                            return (
                                <Tag 
                                    key={key} 
                                    color="green" 
                                    closable 
                                    onClose={() => onKeywordRemove(key, value)}
                                    className="rounded-full"
                                >
                                    {'..' + value.slice(-2)}
                                </Tag>
                            )
                        })
                    }
                </div>

                {/* 筛选和分页 */}
                <Flex gap={2} wrap align='center' justify='space-between'>
                    <Pagination 
                        current={page} 
                        showSizeChanger={false} 
                        simple={{ readOnly: true }} 
                        defaultCurrent={1} 
                        total={total} 
                        onChange={(page) => loadOrderList(page)}
                        className="text-sm"
                    />
                    
                    <Radio.Group 
                        optionType='button' 
                        defaultValue='all' 
                        onChange={(e) => onStatusChange(e.target.value)} 
                        options={[
                            { label: '全部', value: 'all' },
                            { label: '未完付', value: 'paying' },
                            { label: '未完成', value: 'unfinished' },
                        ]}
                        buttonStyle="solid"
                        size="small"
                        className="[&_.ant-radio-button-wrapper]:text-gray-500 [&_.ant-radio-button-wrapper-checked]:text-white"
                    />
                </Flex>
            </div>

            {/* 订单卡片列表 - 响应式网格布局 */}
            <div className="py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3 md:gap-4 justify-items-center">
                    {
                        orderList.map((item, index) => {
                            return (
                                <OrderCard 
                                    onFlowChange={loadOrderList} 
                                    content={item} 
                                    key={index}
                                    className="w-full min-w-[320px] max-w-[420px]"
                                />
                            )
                        })
                    }
                    {
                        orderList.length === 0 && (
                            <Empty className="py-12 col-span-full" description="暂无订单数据" />
                        )                
                    }
                </div>
            </div>

            {/* 底部分页 */}
            <div className="pb-20 md:pb-6">
                <Pagination 
                    current={page} 
                    showSizeChanger={false} 
                    simple={{ readOnly: true }} 
                    defaultCurrent={1} 
                    total={total} 
                    onChange={(page) => loadOrderList(page)}
                    className="flex justify-center"
                />
            </div>
        </div>
    )
}